<!-- eslint-disable vue/no-unused-refs -->
<template>
    <pendo-popover
        v-if="showPendoProducts"
        ref="popoverContent"
        trigger="manual"
        placement="bottom-end"
        :open="isOpen"
        :popover-class="['pendo-products__popover', 'top-nav__popover']"
        @update:open="handlePopoverUpdate">
        <button
            v-pendo-tooltip="{
                content: $t('pendoProducts.Pendo products'),
                arrow: true,
            }"
            data-cy="pendo-products-dropdown-trigger"
            pendo-analytics="pendo-products-nav-item"
            aria-haspopup
            class="top-nav__trigger top-nav__button-trigger"
            :class="{
                'is-open': isOpen,
            }"
            :aria-label="$t('pendoProducts.Pendo products')"
            :aria-expanded="isOpen.toString()"
            @click="handlePopoverUpdate(!isOpen)"
            @keydown.tab="handleTabKeydown"
            @keydown.space.prevent="handlePopoverUpdate(!isOpen)"
            @keydown.enter.prevent="handlePopoverUpdate(!isOpen)"
            @keydown.left.prevent="$emit('left')"
            @keydown.right.prevent="$emit('right')">
            <pendo-products-icon />
        </button>
        <template #popover>
            <div
                ref="pendoProductsContent"
                class="top-nav__popover-content"
                data-cy="pendo-products-dropdown-content"
                @keydown.escape.prevent="handleEscapePopover">
                <h2 class="nav-title">
                    {{ $t('pendoProducts.Pendo products') }}
                </h2>
                <pendo-router-link
                    v-for="(option, index) in dropdownOptions"
                    :key="option.id"
                    tabindex="0"
                    class="nav-option"
                    :pendo-analytics="option.pendoAnalytics"
                    :to="option.route"
                    :target="option.target || '_self'"
                    :external="option.external"
                    @click="handleOptionClick($event)"
                    @keydown.space="handleOptionKeydown($event)"
                    @keydown.enter="handleOptionKeydown($event)"
                    @keydown.tab.prevent="handleOptionKeydown($event)"
                    @keydown.up.prevent="handleArrowKeyNavigation($event, index)"
                    @keydown.down.prevent="handleArrowKeyNavigation($event, index)">
                    <pendo-icon
                        v-if="option.id === 'pendo'"
                        type="circle"
                        class="pendo-products__active-dot"
                        fill="var(--palette-gray-100)"
                        stroke="var(--palette-gray-100)"
                        :size="6" />
                    <div
                        v-pendo-tooltip="{
                            content: tooltipContent,
                            arrow: true,
                            placement: 'top',
                            delay: { show: 1000, hide: 0 },
                        }"
                        class="nav-option__text"
                        @mouseover="onMouseover($event, option)">
                        {{ option.label }}
                    </div>
                </pendo-router-link>
            </div>
        </template>
    </pendo-popover>
</template>

<script>
import { ref } from 'vue';
import { PendoTooltip, PendoPopover, PendoIcon } from '@pendo/components';
import PendoProductsIcon from '@/top-nav/components/icons/pendo-products-icon.vue';
import PendoRouterLink from '@/vue-router/pendo-router-link.vue';

import { eventIsOpeningANewTabOrWindow } from '@/utils/window.utils.js';
import { generateFeedbackSSOUrl } from '@/utils/sso';
import { mapState, mapGetters } from 'vuex';
import { elementHasOverflowX } from '@/utils/dom.utils.js';
import { useArrowKeyNavigation } from '@/common/use/use-arrow-key-navigation/use-arrow-key-navigation';

export default {
    name: 'PendoProductsDropdown',
    components: {
        PendoPopover,
        PendoIcon,
        PendoProductsIcon,
        PendoRouterLink,
    },
    directives: {
        PendoTooltip,
    },
    props: {
        isOpen: {
            type: Boolean,
            default: false,
        },
        preventTabKeyNavigation: {
            type: Boolean,
            default: false,
        },
    },
    emits: ['open', 'close', 'select', 'focusTrigger', 'left', 'right'],
    setup () {
        const pendoProductsContent = ref(null);
        const { handleArrowKeyNavigation } = useArrowKeyNavigation({
            containerRef: pendoProductsContent,
            selector: '.nav-option',
        });

        return {
            pendoProductsContent,
            handleArrowKeyNavigation,
        };
    },
    data () {
        return {
            tooltipContent: null,
        };
    },
    computed: {
        ...mapState({
            hosts: (state) => state.config.hosts,
        }),
        ...mapGetters({
            userCanAccessClassicFeedback: 'auth/userCanAccessClassicFeedback',
        }),
        showPendoProducts () {
            return this.userCanAccessClassicFeedback;
        },
        dropdownOptions () {
            return [
                {
                    id: 'pendo',
                    label: this.$t('pendoProducts.Pendo'),
                },
                {
                    id: 'feedback',
                    label: this.$t('pendoProducts.Feedback (Classic)'),
                    target: '_blank',
                    external: true,
                    route: generateFeedbackSSOUrl('/app', this.hosts),
                    pendoAnalytics: 'legacy-feedback',
                },
            ];
        },
    },
    methods: {
        handleOptionClick ($event) {
            if (eventIsOpeningANewTabOrWindow($event)) return;

            this.handleOptionSelect();
        },
        handleOptionKeydown ($event) {
            if (eventIsOpeningANewTabOrWindow($event)) return;

            $event.preventDefault();
            $event.currentTarget.click();
        },
        handleOptionSelect () {
            this.$emit('select');
        },
        handlePopoverUpdate (open) {
            if (open) {
                this.$emit('open');
                this.focusFirstOption();
            } else {
                this.$emit('close');
            }
        },
        focusFirstOption () {
            setTimeout(() => {
                const firstOption = this.$refs.pendoProductsContent?.querySelector('.nav-option');
                if (firstOption) {
                    firstOption.focus();
                }
            }, 50);
        },
        handleEscapePopover () {
            this.$emit('close');
            this.$emit('focusTrigger');
        },
        onMouseover ({ currentTarget }, option) {
            const { clientWidth, scrollWidth } = currentTarget;
            const hasOverflow = elementHasOverflowX({ clientWidth, scrollWidth });

            if (hasOverflow) {
                this.tooltipContent = option.label;
            } else {
                this.tooltipContent = null;
            }
        },
        handleTabKeydown ($event) {
            if (this.preventTabKeyNavigation) {
                $event.preventDefault();

                this.handlePopoverUpdate(!this.isOpen);
            }
        },
    },
};
</script>

<style lang="less" scoped>
@import '@/side-nav/nav.style.less';

.top-nav__button-trigger:focus:not(:focus-visible) {
    outline: unset;
}

.pendo-products__active-dot {
    margin-right: 8px;

    :deep(svg) {
        height: 8px;
        width: 8px;
    }
}
</style>

<style lang="less">
.top-nav__popover.pendo-products__popover {
    width: 210px;
}
</style>
